import { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiDownload,
  FiEye,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiAlertTriangle,
  FiPlus,
  FiAlertCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const statusStyles = {
  DRAFT: "bg-orange-100 text-orange-700",
  POSTED: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-700",
};

export default function GRNListPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [grns, setGrns] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadGrns() {
      try {
        setIsLoading(true);
        setError("");

        const params = new URLSearchParams();
        if (search.trim()) params.set("search", search.trim());
        if (status) params.set("status", status);

        const query = params.toString();

        const response = await apiRequest(
          `/purchases/grns${query ? `?${query}` : ""}`,
          { accessToken },
        );

        setGrns(Array.isArray(response) ? response : []);
      } catch (requestError) {
        setError(requestError.message || "Could not load GRNs.");
      } finally {
        setIsLoading(false);
      }
    }

    const timer = setTimeout(() => {
      if (accessToken) loadGrns();
    }, 250);

    return () => clearTimeout(timer);
  }, [accessToken, search, status]);

  const filteredGrns = useMemo(() => {
    if (!selectedDate) return grns;

    return grns.filter(
      (grn) =>
        new Date(grn.receivedAt).toISOString().slice(0, 10) === selectedDate,
    );
  }, [grns, selectedDate]);

  const stats = useMemo(
    () => ({
      total: filteredGrns.length,
      posted: filteredGrns.filter((grn) => grn.status === "POSTED").length,
      draft: filteredGrns.filter((grn) => grn.status === "DRAFT").length,
      cancelled: filteredGrns.filter((grn) => grn.status === "CANCELLED")
        .length,
    }),
    [filteredGrns],
  );

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const exportCsv = () => {
    const rows = [
      ["GRN Number", "PO Number", "Supplier", "Received Date", "Items", "Status"],
      ...filteredGrns.map((grn) => [
        grn.grnNumber,
        grn.purchaseOrder?.purchaseOrderNumber || "",
        grn.supplier?.displayName || "",
        formatDate(grn.receivedAt),
        grn._count?.items || 0,
        grn.status,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    link.download = "goods-receipt-notes.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Purchasing / GRN</p>
          <h1 className="mt-1 text-3xl font-bold">Goods Receipt Notes</h1>
          <p className="mt-2 text-gray-500">
            Manage all received inventory from suppliers.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={exportCsv}
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5"
          >
            <FiDownload />
            Export
          </button>

          <button
            type="button"
            onClick={() =>
              navigate("/admindashboard/purchases/grn/create")
            }
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white"
          >
            <FiPlus />
            Create GRN
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <StatCard title="Total GRNs" value={stats.total} icon={FiTruck} color="blue" />
        <StatCard title="Posted" value={stats.posted} icon={FiCheckCircle} color="green" />
        <StatCard title="Drafts" value={stats.draft} icon={FiClock} color="orange" />
        <StatCard title="Cancelled" value={stats.cancelled} icon={FiAlertTriangle} color="red" />
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5">
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search GRN / PO / Supplier"
              className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4"
            />
          </div>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="POSTED">Posted</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">GRN Number</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">PO Number</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Supplier</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Received Date</th>
                <th className="px-6 py-4 text-center text-xs uppercase text-gray-500">Items</th>
                <th className="px-6 py-4 text-center text-xs uppercase text-gray-500">Status</th>
                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!isLoading &&
                filteredGrns.map((grn) => (
                  <tr
                    key={grn.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 font-semibold">{grn.grnNumber}</td>
                    <td className="px-6 py-4">
                      {grn.purchaseOrder?.purchaseOrderNumber || "—"}
                    </td>
                    <td className="px-6 py-4">
                      <p>{grn.supplier?.displayName || "—"}</p>
                      <p className="mt-1 text-xs text-gray-400">
                        {grn.store?.name || ""}
                      </p>
                    </td>
                    <td className="px-6 py-4">{formatDate(grn.receivedAt)}</td>
                    <td className="px-6 py-4 text-center">
                      {grn._count?.items || 0}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <StatusBadge status={grn.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/admindashboard/purchases/grn/${grn.id}`)
                          }
                          className="flex h-9 items-center gap-2 rounded-lg bg-primary-50 px-3 font-medium text-primary-700 transition-all hover:bg-primary-700 hover:text-white"
                        >
                          <FiEye size={15} />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!isLoading && filteredGrns.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-sm text-gray-500">
                    No GRNs found.
                  </td>
                </tr>
              )}

              {isLoading && (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-sm text-gray-500">
                    Loading GRNs...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between p-5">
          <p className="text-sm text-gray-500">
            Showing {filteredGrns.length} GRNs
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-2 text-3xl font-bold">{value}</h3>
        </div>

        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${colors[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}